import { Fragment, type FC } from 'react';
import { Avatar } from '../../../components/ui/Avatar';
import { Cell } from '../../../components/ui/Cell';
import { List } from '../../../components/ui/List';
import { Skeleton } from '../../../components/ui/Skeleton';
import { ItemSource, type WikiDropItemVariant } from '../../../services/generated/Api';
import { variantLabel, wikiItemText, type WikiItemT } from '../wikiItemText';
import { getApiBaseUrl } from '../../../utils/apiBaseUrl';
import { cn } from '../../../utils/cn';
import { exteriorName, kindName, wikiTimeAgo, type WikiT, type WikiTextKey } from '../wikiText';
import { WikiVariantKind, type WikiDrop } from '../wiki.types';
import type { Language } from '../../../i18n/types';

export interface WikiDropsFeed {
  drops: WikiDrop[];
  isLoading: boolean;
}

export const WIKI_DROPS_LIMIT = 10;

const SOURCE_KEY: Partial<Record<ItemSource, WikiTextKey>> = {
  [ItemSource.CASE_BATTLE]: 'dropsCaseBattle',
  [ItemSource.UPGRADE]: 'dropsUpgrade',
  [ItemSource.CONTRACT]: 'dropsContract',
  [ItemSource.RUSH_MID]: 'dropsRushMid',
  [ItemSource.ROULETTE]: 'dropsRoulette',
};

// WHY: имя кейса бэк отдаёт только для выпадений из кейса — обычного, бесплатного в SHOT BAND и подаренного
const dropPlace = (t: WikiT, drop: WikiDrop): string => {
  if (drop.caseName) return t('dropsCase', { name: drop.caseName });
  return t(SOURCE_KEY[drop.source] ?? 'typeContainer');
};

// WHY: у предмета вместо износа — эффект, цвет или StatTrak™ и слэб; подписи те же, что в вариантах на странице предмета.
// Если у предмета есть StatTrak™, обычная версия тоже подписывается — иначе строка «без подписи» читалась как неизвестная версия
const itemVersion = (t: WikiT, ti: WikiItemT, variant: WikiDropItemVariant, labelPlain: boolean): string[] => [
  ...(labelPlain || variant.tint || variant.effect || variant.stattrak ? [variantLabel(t, ti, variant)] : []),
  ...(variant.slab ? [ti('groupSlab')] : []),
];

const dropDetails = (t: WikiT, ti: WikiItemT, drop: WikiDrop, labelPlain: boolean): string[] => [
  ...(drop.itemVariant
    ? itemVersion(t, ti, drop.itemVariant, labelPlain)
    : [
        ...(drop.kind === WikiVariantKind.Normal ? [] : [kindName(t, drop.kind)]),
        ...(drop.exterior ? [exteriorName(t, drop.exterior)] : []),
      ]),
  dropPlace(t, drop),
];

function splitName(userName: string): { firstName: string; lastName?: string } {
  const [firstName, ...rest] = userName.split(' ');
  return { firstName, lastName: rest.join(' ') || undefined };
}

const SKELETON_ROWS = [0, 1, 2];

interface WikiRecentDropsProps extends WikiDropsFeed {
  lang: Language;
  t: WikiT;
  onOpenUser: (userId: string) => void;
  labelPlain?: boolean;
  className?: string;
}

export const WikiRecentDrops: FC<WikiRecentDropsProps> = ({ drops, isLoading, lang, t, onOpenUser, labelPlain = false, className }) => {
  if (!isLoading && drops.length === 0) return null;
  const rows = drops.slice(0, WIKI_DROPS_LIMIT);
  const now = Date.now();
  const ti = wikiItemText(lang);

  return (
    <section data-nosnippet aria-labelledby="wiki-drops" className={cn('flex flex-col gap-2', className)}>
      <h2 id="wiki-drops" className="m-0 text-[22px] font-bold leading-[28px] text-label-primary">
        {t('dropsTitle')}
      </h2>
      <p className="m-0 text-[13px] leading-[18px] text-label-secondary">{t('dropsHint')}</p>
      <List className="rounded-[18px]">
        {isLoading
          ? SKELETON_ROWS.map((row) => (
              <Cell
                key={row}
                before={<Skeleton className="size-8 rounded-full" />}
                after={<Skeleton className="h-3 w-12" />}
                subtitle={<Skeleton className="mt-1 h-3 w-40" />}
                chevron
                isLast={row === SKELETON_ROWS.length - 1}
              >
                <Skeleton className="h-3 w-28" />
              </Cell>
            ))
          : rows.map((drop, index) => {
              const details = dropDetails(t, ti, drop, labelPlain);
              return (
                <Cell
                  key={drop.id}
                  before={
                    <Avatar
                      src={`${getApiBaseUrl()}/api/users/avatar/${drop.userId}`}
                      size="s"
                      fallbackKey={drop.userId}
                      {...splitName(drop.userName)}
                    />
                  }
                  subtitle={
                    // WHY: версия и место не влезают в строку на телефоне — переносим между частями, а не обрезаем место выпадения
                    <span className="whitespace-normal">
                      {details.map((part, i) => (
                        <Fragment key={part}>
                          {i > 0 && ' '}
                          <span className="whitespace-nowrap">
                            <bdi>{part}</bdi>
                            {i < details.length - 1 && ' ·'}
                          </span>
                        </Fragment>
                      ))}
                    </span>
                  }
                  after={<span className="text-sm">{wikiTimeAgo(t, lang, drop.acquiredAt, now)}</span>}
                  chevron
                  isLast={index === rows.length - 1}
                  onClick={() => onOpenUser(drop.userId)}
                >
                  <bdi>{drop.userName}</bdi>
                </Cell>
              );
            })}
      </List>
    </section>
  );
};
